/**
 * The Standard's gallery.
 *
 * A stack of images with numbered counters above them. It advances on its own
 * and loops, and clicking a number jumps to that image.
 *
 * Opacity is the only thing that moves, so no layout work happens per frame,
 * and the counters are real buttons rather than styled spans so the keyboard
 * reaches them for free.
 *
 * Two things it does that a plain timer would not:
 *
 * 1. It only runs while the section is on screen. The gallery sits near the
 *    foot of the page, and a timer firing every few seconds against a section
 *    nobody has scrolled to yet is work for nothing. That is an economy rather
 *    than a prerequisite: it starts running first and the observer pauses it,
 *    so a browser that never reports intersection still gets a gallery that
 *    moves.
 * 2. A click restarts the clock rather than stopping it. Leaving the interval
 *    alone means a click a moment before it fires is overtaken immediately,
 *    which reads as the gallery ignoring you.
 *
 * Under reduced motion it does not advance at all — the counters still work,
 * so nothing becomes unreachable, it simply never moves on its own.
 */

const REDUCED = '(prefers-reduced-motion: reduce)';

class TheStandard extends HTMLElement {
  #index = 0;
  #timer = null;
  #visible = true;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-standard-slide]'));
    this.counters = Array.from(this.querySelectorAll('[data-standard-counter]'));
    if (this.slides.length === 0) return;

    this.reduced = window.matchMedia(REDUCED);
    this.interval = Number(this.getAttribute('autoplay')) || 0;

    this.#index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.#index < 0) this.#index = 0;

    this.counters.forEach((counter, i) => {
      counter.addEventListener('click', () => this.goTo(i));
    });

    this.reduced.addEventListener('change', this.#sync);

    // Started before the observer is attached, and deliberately so. The
    // observer is an economy, not a prerequisite: if it never reports — an
    // old browser, a background tab that withholds callbacks — the gallery
    // still runs rather than sitting silently still.
    this.#sync();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.#visible = entry.isIntersecting;
          this.#sync();
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#pause();
    this.observer?.disconnect();
    this.reduced?.removeEventListener('change', this.#sync);
  }

  get index() {
    return this.#index;
  }

  set index(value) {
    const count = this.slides.length;
    const next = ((value % count) + count) % count;
    if (next === this.#index) return;

    this.slides[this.#index]?.classList.remove('is-active');
    this.slides[this.#index]?.setAttribute('aria-hidden', 'true');
    this.counters[this.#index]?.setAttribute('aria-current', 'false');

    this.#index = next;

    this.slides[next]?.classList.add('is-active');
    this.slides[next]?.removeAttribute('aria-hidden');
    this.counters[next]?.setAttribute('aria-current', 'true');
  }

  next() {
    this.index = this.#index + 1;
  }

  goTo(value) {
    this.index = value;
    if (this.#timer) this.#play();
  }

  #sync = () => {
    const should =
      this.#visible && this.interval > 0 && this.slides.length > 1 && !this.reduced.matches;
    if (should) this.#play();
    else this.#pause();
  };

  #play() {
    this.#pause();
    this.#timer = window.setInterval(() => this.next(), this.interval);
  }

  #pause() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('the-standard')) {
  customElements.define('the-standard', TheStandard);
}

/**
 * The same gallery under a second name.
 *
 * The About page's origin band carries the identical widget — a stack of
 * images with numbered counters that advances on its own — in a different
 * place and at a different size. It is the behaviour that is shared, not the
 * band, so rather than copy a hundred lines it registers the same class again.
 * A custom element name can only be claimed once, hence the empty subclass.
 */
class CounterGallery extends TheStandard {}

if (!customElements.get('counter-gallery')) {
  customElements.define('counter-gallery', CounterGallery);
}
